import { deburr } from 'lodash-es'
import { GeoLocation, LocationArea, LocationItem, LocationsSort } from './types'

export const getLocationName = (location: GeoLocation): string => location.name || location.label || ''

export const normalizeSearch = (text: string): string => deburr(text).toLowerCase().trim()

const collator = new Intl.Collator('fr', { sensitivity: 'base' })

export const toLocationItems = (locations: GeoLocation[], areas?: Array<LocationArea | undefined>): LocationItem[] =>
  locations.map((location, index) => ({
    index,
    name: getLocationName(location),
    radius: location.radius,
    area: areas?.[index],
  }))

/** Matches the name, the area label or the area code ("78" finds the Yvelines), ignoring case and accents */
export const filterLocationItems = (items: LocationItem[], query: string): LocationItem[] => {
  const needle = normalizeSearch(query)
  if (!needle) return items
  return items.filter(
    (item) =>
      normalizeSearch(item.name).includes(needle) ||
      (item.area && (normalizeSearch(item.area.label).includes(needle) || item.area.code.toLowerCase() === needle))
  )
}

const byName = (a: LocationItem, b: LocationItem): number => collator.compare(a.name, b.name)

// Only the primary key follows the sort direction: the name tie-break stays A to Z
const PRIMARY: Record<LocationsSort['key'], (a: LocationItem, b: LocationItem) => number> = {
  added: (a, b) => a.index - b.index,
  name: byName,
  area: (a, b) => (a.area && b.area ? a.area.code.localeCompare(b.area.code, 'fr', { numeric: true }) : 0),
  radius: (a, b) => (a.radius ?? 0) - (b.radius ?? 0),
}

export const sortLocationItems = (items: LocationItem[], sort: LocationsSort): LocationItem[] => {
  const direction = sort.dir === 'desc' ? -1 : 1
  const primary = PRIMARY[sort.key]
  return [...items].sort((a, b) => {
    // Locations without an area always go last, whatever the direction
    if (sort.key === 'area' && Boolean(a.area) !== Boolean(b.area)) return a.area ? -1 : 1
    return primary(a, b) * direction || byName(a, b)
  })
}

export interface LocationsPage {
  items: LocationItem[]
  /** 1-based, clamped to the existing pages */
  page: number
  pageCount: number
  /** 0-based index of the first item of the page */
  start: number
}

export const paginate = (items: LocationItem[], page: number, pageSize: number): LocationsPage => {
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize))
  const current = Math.min(Math.max(1, page), pageCount)
  const start = (current - 1) * pageSize
  return { items: items.slice(start, start + pageSize), page: current, pageCount, start }
}
